import angular from 'angular'
import lodash from 'lodash'

import {
  SERVER_ENVIRONMENT_SELECT,
  INTEGRATION_TYPE,
  STATUS_LIST,
  APPLICATION_IMPORTANCE_LIST,
  INTEGRATION_APPLICATION_OPTIONS,
  RULE_SEVERITY_LIST
} from '../jenkins-controller/legacy-constants'

export function JenkinsService(
  $location,
  $q,
  $cacheFactory,
  UserService,
  rulesJobOutcomePolicyService,
  ServiceUtils,
  JobOutcomePolicyFactory
) {
  'ngInject'

  let cache
  const _undefinedRepo = '$undefined'
  const _cacheId = 'jenkinsVals'

  return {
    getJenkinsJobPolicyOutcome,
    addJenkinsJobOutcomePolicy,
    refreshJenkinsJobPolicyOutcome,
    getJenkinsJobPolicyOutcomeForVulnerability,
    getJenkinsJobPolicyOutcomeForLibrary,
    refreshExistingJenkinsJobPolicyOutcome,
    testExistingJenkinsJobPolicyOutcome,
    addJenkinsJobPolicyOutcome,
    editJenkinsJobOutcomePolicy,
    buildJenkinsJobOutcomePolicy,
    buildJenkinsIntegration,
    addJenkinsIntegration,
    transformToIntegrationView,
    clearCache
  }

  function getJenkinsJobPolicyOutcome(id) {
    return JobOutcomePolicyFactory.getJenkinsJobOutcomePolicy(id)
      .then((response) => {
        return {
          jobOutcomePolicy: response.data.policy
        }
      })
      .catch(ServiceUtils.handleError)
  }

  function refreshJenkinsJobPolicyOutcome(data) {
    if (_areJenkinsValuesCached(data)) {
      return _getCachedValuesAsPromise(data)
    }
    return JobOutcomePolicyFactory.refreshJenkinsJobPolicyOutcome(data).then(
      (response) => {
        _cacheJenkinsValues(
          response.data.settings,
          angular.isDefined(data.repository)
        )
        return response.data.settings
      },
      (response) => {
        return $q.reject(response.data)
      }
    )
  }

  function addJenkinsJobOutcomePolicy(request) {
    return JobOutcomePolicyFactory.addJobOutcomePolicy(request)
      .then((response) => {
        ServiceUtils.logSuccess(response)
        $location.path(UserService.buildUrl('/settings/integration'), true)
      })
      .catch(ServiceUtils.handleError)
  }

  function getJenkinsJobPolicyOutcomeForVulnerability(id, appId) {
    return JobOutcomePolicyFactory.getJenkinsJobPolicyOutcomeForVulnerability(
      id,
      appId
    ).then(
      (response) => {
        return {
          jobOutcomePolicy: response.data.jobOutcomePolicy,
          settings: response.data.settings
        }
      },
      (response) => {
        return $q.reject(response.data)
      }
    )
  }

  function getJenkinsJobPolicyOutcomeForLibrary(jobOutcomePolicyId) {
    return JobOutcomePolicyFactory.getJenkinsJobPolicyOutcomeForLibrary(
      jobOutcomePolicyId
    )
      .then((response) => {
        return {
          jobOutcomePolicy: response.data.jobOutcomePolicy,
          settings: response.data.settings
        }
      })
      .catch(ServiceUtils.handleError)
  }

  function refreshExistingJenkinsJobPolicyOutcome(id, data) {
    if (_areJenkinsValuesCached(data)) {
      return _getCachedValuesAsPromise(data)
    }
    return JobOutcomePolicyFactory.refreshExistingJenkinsJobPolicyOutcome(
      id,
      data
    ).then(
      (response) => {
        _cacheJenkinsValues(
          response.data.settings,
          angular.isDefined(data.repository)
        )
        return response.data.settings
      },
      (response) => {
        return $q.reject(response.data)
      }
    )
  }

  function testExistingJenkinsJobPolicyOutcome() {
    return JobOutcomePolicyFactory.getJobOutcomePolicy().then(
      (response) => {
        return response.data
      },
      (response) => {
        return $q.reject(response.data)
      }
    )
  }

  function addJenkinsJobPolicyOutcome(jobOutcomePolicy) {
    return JobOutcomePolicyFactory.addJenkinsJobPolicyOutcome(jobOutcomePolicy)
      .then((response) => {
        ServiceUtils.logSuccess(response)
        _destroyCache()
        $location.path(UserService.buildUrl('/settings/integration'), true)
      })
      .catch(ServiceUtils.handleError)
  }

  function editJenkinsJobOutcomePolicy(id, jobOutcomePolicy) {
    return JobOutcomePolicyFactory.editJenkinsJobOutcomePolicy(
      id,
      jobOutcomePolicy
    )
      .then((response) => {
        ServiceUtils.logSuccess(response)
        _destroyCache()
        $location.path(UserService.buildUrl('/settings/integration'), true)
      })
      .catch(ServiceUtils.handleError)
  }

  function addJenkinsIntegration(data, properties) {
    data.jenkins_fields = {}

    if (properties.assignees) {
      data.jenkins_fields.assignees = lodash.map(properties.assignees, 'login')
    }
    if (properties.labels) {
      data.jenkins_fields.labels = lodash.map(properties.labels, 'name')
    }
    if (properties.milestone) {
      data.jenkins_fields.milestone = properties.milestone.number
    }
  }

  function transformToIntegrationView(integration, rules) {
    const form = {
      name: integration.name,
      host: integration.host,
      username: integration.username,
      password: integration.password,
      auth_type: integration.auth_type,
      applications: integration.applications,
      all_applications: integration.all_applications,
      assignee: integration.assignee,
      issue_type: integration.issue_type,
      project: integration.project,

      jenkins_organization: integration.jenkins_organization,
      repository: integration.repository,

      labels: integration.labels,
      assignees: integration.assignees,
      milestone: integration.milestone,

      auto_create_tickets_enabled: integration.auto_create_tickets_enabled,
      auto_create_tickets_rules: [],
      auto_create_tickets_severities: [],
      auto_create_tickets_all_rules: integration.auto_create_tickets_all_rules,

      integration: integration.integration,
      integrations: integration.integrations,
      placeholderURL: integration.placeholderURL
    }

    if (!form.auto_create_tickets_all_rules) {
      rulesJobOutcomePolicyService.transformRulesToIntegrationView(
        integration,
        rules,
        form
      )
    }

    return form
  }

  function clearCache() {
    _destroyCache()
  }

  function _getCachedValuesAsPromise(data) {
    const deferred = $q.defer()
    deferred.resolve(_getCachedValues(data))
    return deferred.promise
  }

  function _areJenkinsValuesCached(data) {
    _initCache()
    const id = _getCacheIdForJenkinsRequest(data.jenkins_org, data.repository)
    return angular.isDefined(cache.get(id))
  }

  function _getCachedValues(data) {
    _initCache()
    const id = _getCacheIdForJenkinsRequest(data.jenkins_org, data.repository)
    return cache.get(id)
  }

  function _cacheJenkinsValues(settings, isRepoData) {
    _initCache()
    const org = settings.selected_organization.organization
    // If the values to be cached only contain repository data
    const repo = isRepoData
      ? settings.selected_organization.repository_settings.repository
      : undefined
    const id = _getCacheIdForJenkinsRequest(org, repo)
    cache.put(id, settings)
  }

  function _getCacheIdForJenkinsRequest(jenkinsOrg, repository) {
    if (angular.isDefined(repository)) {
      return jenkinsOrg + '-' + repository
    }
    return jenkinsOrg + '-' + _undefinedRepo
  }

  function _initCache() {
    if (angular.isUndefined(cache)) {
      cache = $cacheFactory(_cacheId)
    }
  }

  function _destroyCache() {
    if (angular.isDefined(cache)) {
      cache.destroy()
      cache = undefined
    }
  }
}

export function buildJenkinsIntegration(model) {
  const request: any = {}

  request.all_environments = model.all_environments
  request.name = model.name
  request.outcome = model.outcome
  request.enabled = model.overridePolicy
  request.all_applications = model.all_applications
  request.app_criterion = model.app_criterion
  request.status_filter = lodash.map(model.status, 'value')
  request.keycode = INTEGRATION_TYPE.JENKINS
  request.include_all_start_dates = model.include_all_start_dates

  request.start_date = model.start_date.value
  request.end_date = model.end_date.value

  request.custom_end_date = model.custom_end_date
  request.custom_start_date = model.custom_start_date

  request.start_date = model.start_date
  request.end_date = model.end_date

  request.is_job_start_time = model.is_job_start_time

  request.opt_into_query = model.opt_into_query

  request.environments =
    model.environments[0].value === 'ALL'
      ? []
      : lodash.map(model.environments, 'value')

  const severities = {}
  const rules = {}
  request.all_rules = model.all_rules_threshold

  if (model.rulesAndSeverities) {
    model.rulesAndSeverities = model.rulesAndSeverities.filter(
      (ruleAndSeverity) => {
        return ruleAndSeverity.name !== ''
      }
    )
  }

  const severityList = lodash.filter(
    model.rulesAndSeverities,
    (ruleOrSeverity) => {
      return lodash
        .map(RULE_SEVERITY_LIST, 'value')
        .includes(ruleOrSeverity.value)
    }
  )

  const ruleList = lodash.filter(model.rulesAndSeverities, (ruleOrSeverity) => {
    return !lodash
      .map(RULE_SEVERITY_LIST, 'value')
      .includes(ruleOrSeverity.value)
  })

  lodash.forEach(severityList, (severity) => {
    severities[severity.value] = severity.number >= 0 ? severity.number : -1
  })

  lodash.forEach(ruleList, (rule) => {
    if (rule.value === 'ALL') {
      request.all_rules = true
      request.all_rules_threshold = rule.number >= 0 ? rule.number : -1
    } else {
      rules[rule.value] = rule.number >= 0 ? rule.number : -1
    }
  })

  request.severities = severities
  request.rules = rules

  if (model.app_criterion === 'ID') {
    request.all_applications
      ? (request.app_ids = [])
      : (request.app_ids = lodash.map(model.applicationChoice, 'app_id'))
  } else if (model.app_criterion === 'IMPORTANCE') {
    if (request.all_applications) {
      request.app_ids = []
      request.importances = lodash.map(model.applicationChoice, 'value')
    }
  } else if (model.app_criterion === 'APP_TAGS') {
    if (request.all_applications) {
      request.app_ids = []
      request.application_tags = {}
      request.application_tags = lodash.map(model.applicationChoice, 'value')
    }
  }
  return request
}

export function buildJenkinsJobOutcomePolicy(model) {
  const jobOutcomePolicy: any = {}
  jobOutcomePolicy.name = model.name
  jobOutcomePolicy.editName = model.name

  jobOutcomePolicy.outcome = model.outcome
  jobOutcomePolicy.overridePolicy = model.enabled

  jobOutcomePolicy.all_applications = model.all_applications
  jobOutcomePolicy.app_criterion = model.app_criterion

  jobOutcomePolicy.status = []

  jobOutcomePolicy.start_date = model.start_date
  jobOutcomePolicy.end_date = model.end_date

  jobOutcomePolicy.custom_end_date = model.custom_end_date
  jobOutcomePolicy.custom_start_date = model.custom_start_date

  jobOutcomePolicy.include_all_start_dates = model.include_all_start_dates
  jobOutcomePolicy.is_job_start_time = model.is_job_start_time

  jobOutcomePolicy.opt_into_query = model.opt_into_query

  jobOutcomePolicy.all_environments = model.all_environments
  jobOutcomePolicy.environments = []

  if (model.all_environments) {
    jobOutcomePolicy.environments = SERVER_ENVIRONMENT_SELECT
  }

  for (const environment of model.environments) {
    for (const environmentListItem of SERVER_ENVIRONMENT_SELECT) {
      if (environment === environmentListItem.value) {
        jobOutcomePolicy.environments.push(environmentListItem)
      }
    }
  }

  let statusFilterObject = {}
  for (const status of model.status_filter) {
    for (const statusListItem of STATUS_LIST) {
      if (statusListItem.value.toString() === status.toUpperCase()) {
        statusFilterObject = statusListItem
        jobOutcomePolicy.status[model.status_filter.indexOf(status)] =
          statusFilterObject
      }
    }
  }

  if (model.app_criterion === 'ID') {
    jobOutcomePolicy.applicationOptions = angular.copy(
      INTEGRATION_APPLICATION_OPTIONS
    )[0]
    jobOutcomePolicy.importance = angular.copy(APPLICATION_IMPORTANCE_LIST)
    if (model.all_applications) {
      jobOutcomePolicy.applicationChoice = model.applications
      jobOutcomePolicy.applications = []
    } else {
      jobOutcomePolicy.applicationChoice = model.applications
      jobOutcomePolicy.applications = model.applications
    }
  } else if (model.app_criterion === 'IMPORTANCE') {
    jobOutcomePolicy.applicationOptions = angular.copy(
      INTEGRATION_APPLICATION_OPTIONS
    )[1]
    jobOutcomePolicy.importance = []
    let importanceObject = {}
    for (const importance of model.importances) {
      for (const importanceListItem of APPLICATION_IMPORTANCE_LIST) {
        if (importanceListItem.value.toString() === importance.toUpperCase()) {
          importanceObject = importanceListItem
          jobOutcomePolicy.importance[model.importances.indexOf(importance)] =
            importanceObject
        }
      }
    }

    jobOutcomePolicy.applicationChoice = jobOutcomePolicy.importance
  } else if (model.app_criterion === 'APP_TAGS') {
    jobOutcomePolicy.importance = angular.copy(APPLICATION_IMPORTANCE_LIST)
    jobOutcomePolicy.applicationOptions = angular.copy(
      INTEGRATION_APPLICATION_OPTIONS
    )[2]
    const tags: any = []
    for (const tag of model.application_tags) {
      tags.push({
        name: tag,
        value: tag
      })
    }
    jobOutcomePolicy.application_tags = tags
    jobOutcomePolicy.applicationChoice = tags
  }

  jobOutcomePolicy.rules = model.rules
  jobOutcomePolicy.severities = model.severities

  jobOutcomePolicy.all_rules = model.all_rules
  jobOutcomePolicy.all_rules_threshold = model.all_rules_threshold

  return jobOutcomePolicy
}
